import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db, logout } from '../firebase';
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { cancelSubscription, deleteAccount } from '../api';
import { User, Shield, Save, Check, CreditCard, XCircle, AlertTriangle, Briefcase, Trash2 } from 'lucide-react';
import { EcointelLottieMark } from '../components/EcoIntelLoader';
import AddressFieldsSection from '../components/AddressFieldsSection';
import ConsentsCard from '../components/ConsentsCard';
import {
  ADDRESS_FIELD_DEFAULTS,
  composeFullAddress,
  profileDataToAddressState,
} from '../utils/profileAddress';
import {
  BUSINESS_TYPES,
  BUSINESS_TYPE_OTHER,
  isValidBusinessType,
} from '../constants/onboardingConsents';
import { ROLES, ROLE_LABELS, ROLE_BADGE_STYLES, SUBSCRIPTION_LABELS, SUBSCRIPTION_BADGE_STYLES, SUBSCRIPTION_STATUS, hasActiveAccess, mustCancelSubscriptionBeforeAccountDelete } from '../constants/roles';
import { useUserFacingError } from '../hooks/useUserFacingError';

export default function UserProfile() {
  const [profile, setProfile] = useState(null);
  const [displayName, setDisplayName] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [businessType, setBusinessType] = useState('');
  const [businessTypeOther, setBusinessTypeOther] = useState('');
  const [addressFields, setAddressFields] = useState(() => ({ ...ADDRESS_FIELD_DEFAULTS }));
  const [jobTitle, setJobTitle] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState(null);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [deleteAccountError, setDeleteAccountError] = useState(null);
  const navigate = useNavigate();
  const { formatError, logError } = useUserFacingError();

  // Real-time profile subscription. Replaces a one-shot `getDoc` so
  // any change to users/{uid} — self-cancel, admin override toggle,
  // Stripe webhook flipping subscriptionStatus → 'cancelling', period-
  // end mirror landing — is reflected in the UI within a few hundred
  // ms without a manual page refresh. Critical for the cancel-
  // subscription flow: the cancelling banner and the (hidden)
  // Cancel-Subscription button both pivot on `profile.subscriptionStatus`,
  // so the moment /cancel-subscription's inline mirror commits the
  // doc, this listener fires and the UI updates in place.
  //
  // We intentionally do NOT re-hydrate the editable form fields
  // (displayName, businessName, addressFields, jobTitle) on every
  // snapshot — that would clobber unsaved local edits. They're seeded
  // once on initial mount via the `!profile` guard. The read-only
  // Subscription & Access card pulls fresh data on every snapshot.
  useEffect(() => {
    if (!auth.currentUser) return undefined;
    const docRef = doc(db, 'users', auth.currentUser.uid);
    const unsub = onSnapshot(
      docRef,
      (snap) => {
        if (!snap.exists()) {
          setProfile(null);
          return;
        }
        const data = snap.data();
        setProfile((prev) => {
          if (!prev) {
            setDisplayName(data.displayName || auth.currentUser.displayName || '');
            setBusinessName(data.businessName || '');
            setBusinessType(isValidBusinessType(data.businessType) ? data.businessType : '');
            setBusinessTypeOther(data.businessTypeOther || '');
            setAddressFields(profileDataToAddressState(data));
            setJobTitle(data.jobTitle || '');
          }
          return data;
        });
      },
      (err) => {
        console.error('UserProfile snapshot error:', err);
      }
    );
    return () => unsub();
  }, []);

  const handleSaveProfile = async () => {
    if (!auth.currentUser) return;
    const uid = auth.currentUser.uid;
    setSaving(true);
    try {
      const patch = {
        displayName: displayName.trim(),
        businessName: businessName.trim(),
        businessType: isValidBusinessType(businessType) ? businessType : '',
        businessTypeOther: businessType === BUSINESS_TYPE_OTHER ? businessTypeOther.trim() : '',
        address: composeFullAddress(addressFields),
        addressLine1: addressFields.addressLine1.trim(),
        addressLine2: addressFields.addressLine2.trim(),
        addressCity: addressFields.addressCity.trim(),
        addressRegion: addressFields.addressRegion.trim(),
        addressPostcode: addressFields.addressPostcode.trim(),
        addressCountry: addressFields.addressCountry.trim(),
        jobTitle: jobTitle.trim(),
      };
      // Marketing consent is no longer saved here: it moved to the Consents &
      // permissions card (ConsentsCard), which owns it as an immediate-save
      // toggle writing an append-only record + the marketingOptIn mirror field.
      await updateDoc(doc(db, "users", uid), patch);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      console.error('Failed to save profile:', e);
      alert('Failed to save profile: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (mustCancelSubscriptionBeforeAccountDelete(profile)) {
      window.alert(
        'Cancel your subscription first using the Cancel Subscription button above. '
        + 'Once your status shows Cancelling, you can delete your account. '
        + 'No further renewals are charged after you cancel.'
      );
      return;
    }

    const email = auth.currentUser?.email || 'your account';
    const subClause = profile?.subscriptionStatus === SUBSCRIPTION_STATUS.CANCELLING
      ? '\n\nYour subscription is already set to end and will not renew.'
      : '';
    const ok = window.confirm(
      `Permanently delete ${email}?${subClause}\n\n`
      + 'This removes your profile and sign-in access. Your property data in the app is not deleted by this action. This cannot be undone.'
    );
    if (!ok) return;

    const okFinal = window.confirm('This is your last chance to keep your account. Delete it permanently?');
    if (!okFinal) return;

    setDeletingAccount(true);
    setDeleteAccountError(null);
    try {
      const result = await deleteAccount();
      if (result?.auth_deleted === false) {
        console.warn('[UserProfile] Account doc removed but Firebase Auth delete reported an error:', result.auth_delete_error);
      }
      try {
        await logout();
      } catch (signOutErr) {
        console.warn('[UserProfile] Sign-out after delete failed (non-fatal):', signOutErr);
      }
      navigate('/login', { replace: true });
    } catch (err) {
      logError('Delete account failed', err);
      setDeleteAccountError(formatError(err));
    } finally {
      setDeletingAccount(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? You will retain access until the end of the current billing period.')) {
      return;
    }
    setCancelling(true);
    setCancelError(null);
    try {
      // /cancel-subscription inline-mirrors subscriptionStatus →
      // 'cancelling' + subscriptionCurrentPeriodEnd on the user doc
      // BEFORE returning (see backend/analytics-server/index.js). The
      // onSnapshot listener above picks that up automatically — no
      // manual re-fetch required.
      await cancelSubscription();
    } catch (err) {
      logError('Cancel subscription failed', err);
      setCancelError(formatError(err));
    } finally {
      setCancelling(false);
    }
  };

  if (!profile) return <div className="p-8">Loading profile...</div>;

  const role = profile.role || ROLES.USER;
  const subStatus = profile.subscriptionStatus || SUBSCRIPTION_STATUS.NONE;
  const isOverridden = !!profile.subscriptionOverride;
  const isActive = hasActiveAccess(role, subStatus, isOverridden);
  const hasPaidSub = !!profile.stripeSubscriptionId;
  const isCancelling = subStatus === 'cancelling';
  const isAdminUser = role === ROLES.ADMIN;
  // Set inline by /cancel-subscription + /admin/cancel-user-subscription
  // and reaffirmed by the customer.subscription.updated webhook (see
  // backend/analytics-server/index.js). Falls back to a generic
  // "end of your current billing period" string if Stripe hasn't given
  // us a period_end yet (e.g. very old account whose cancel pre-dated
  // this field landing on the user doc).
  const cancellingEndDate = (() => {
    if (!isCancelling) return null;
    const raw = profile?.subscriptionCurrentPeriodEnd;
    if (!raw) return null;
    const d = new Date(raw);
    return isNaN(d.getTime()) ? null : d;
  })();
  const cancellingEndDateLabel = cancellingEndDate
    ? cancellingEndDate.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
    : null;
  const mustCancelBeforeDelete = mustCancelSubscriptionBeforeAccountDelete(profile);
  const canDeleteAccount = !isAdminUser && !mustCancelBeforeDelete;

  return (
    <div className="max-w-2xl mx-auto p-8">
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="bg-[#043132] p-6 text-white flex items-center gap-4">
          <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
            <User size={24} />
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-bold">{displayName || auth.currentUser?.displayName || 'User'}</h2>
            <p className="text-white/60 text-sm">{auth.currentUser?.email}</p>
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-bold ${ROLE_BADGE_STYLES[role] || 'bg-slate-100 text-slate-600'}`}>
            {ROLE_LABELS[role] || role}
          </span>
        </div>

        <div className="p-6 space-y-6">
          {/* Profile Settings */}
          <div>
            <h3 className="font-bold text-slate-800 mb-4">Profile Settings</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Display Name</label>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Your name"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:border-[#043132] focus:ring-1 focus:ring-[#043132] outline-none"
                />
                <p className="text-[11px] text-slate-400 mt-1">Shown in the welcome banner and across the app.</p>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Company / Business Name</label>
                <input
                  type="text"
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  placeholder="e.g. EcoIntel Ltd"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:border-[#043132] focus:ring-1 focus:ring-[#043132] outline-none"
                  autoComplete="organization"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Business type</label>
                <select
                  value={businessType}
                  onChange={(e) => setBusinessType(e.target.value)}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:border-[#043132] focus:ring-1 focus:ring-[#043132] outline-none"
                >
                  <option value="" disabled>Select your business type…</option>
                  {BUSINESS_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
                {businessType === BUSINESS_TYPE_OTHER && (
                  <input
                    type="text"
                    value={businessTypeOther}
                    onChange={(e) => setBusinessTypeOther(e.target.value)}
                    placeholder="Please specify your business type"
                    className="w-full mt-2 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:border-[#043132] focus:ring-1 focus:ring-[#043132] outline-none"
                  />
                )}
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                  <Briefcase size={12} className="text-slate-400" /> Job title
                </label>
                <input
                  type="text"
                  value={jobTitle}
                  onChange={(e) => setJobTitle(e.target.value)}
                  placeholder="e.g. Farm manager"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:border-[#043132] focus:ring-1 focus:ring-[#043132] outline-none"
                  autoComplete="organization-title"
                />
              </div>
              <AddressFieldsSection
                value={addressFields}
                onChange={(patch) => setAddressFields((prev) => ({ ...prev, ...patch }))}
                required={false}
              />
              <button
                onClick={handleSaveProfile}
                disabled={saving}
                className="px-5 py-2 bg-[#043132] text-white text-sm font-medium rounded-lg hover:bg-[#065354] flex items-center gap-2 disabled:opacity-50"
              >
                {saving ? <EcointelLottieMark size={14} /> : saved ? <Check size={14} /> : <Save size={14} />}
                {saving ? 'Saving...' : saved ? 'Saved' : 'Save Changes'}
              </button>
            </div>
          </div>

          {/* Subscription Status */}
          <div className="border-t border-slate-100 pt-6">
            <h3 className="font-bold text-slate-900 mb-4">Subscription & Access</h3>

            <div className="bg-slate-50 rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-600">Status</span>
                <span className={`px-3 py-1 rounded-full text-xs font-bold ${SUBSCRIPTION_BADGE_STYLES[subStatus] || 'bg-slate-100 text-slate-600'}`}>
                  {SUBSCRIPTION_LABELS[subStatus] || subStatus || 'No Subscription'}
                </span>
              </div>

              {isOverridden && (
                <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 px-3 py-2 rounded-lg">
                  <Shield size={14} />
                  <span>Admin-granted access{profile.overrideReason ? ` · ${profile.overrideReason}` : ''}</span>
                </div>
              )}

              {isCancelling && (
                <div className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 px-3 py-2 rounded-lg">
                  <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                  <span>
                    {cancellingEndDateLabel
                      ? <>Access continues until <strong>{cancellingEndDateLabel}</strong>, then reverts to the free tier.</>
                      : 'Access continues until the end of your current billing period, then reverts to the free tier.'}
                  </span>
                </div>
              )}
            </div>

            {/* Subscription Actions */}
            {!isAdminUser && !isOverridden && (
              <div className="mt-4">
                {!isActive && (
                  <button
                    onClick={() => navigate('/checkout')}
                    className="w-full py-3 px-4 bg-[#043132] hover:bg-[#065354] text-white font-bold rounded-xl transition-colors flex items-center justify-center gap-2"
                  >
                    <CreditCard size={16} /> Subscribe · £50/month
                  </button>
                )}

                {hasPaidSub && subStatus === SUBSCRIPTION_STATUS.ACTIVE && (
                  <div className="mt-3 space-y-2">
                    <button
                      onClick={handleCancelSubscription}
                      disabled={cancelling}
                      className="text-sm text-red-600 hover:text-red-700 font-medium flex items-center gap-1.5 transition-colors"
                    >
                      {cancelling ? <EcointelLottieMark size={14} /> : <XCircle size={14} />}
                      {cancelling ? 'Cancelling...' : 'Cancel Subscription'}
                    </button>
                    <p className="text-xs text-slate-500">
                      Cancels at the end of your current billing period. Required before you can delete your account.
                    </p>
                    {cancelError && (
                      <p className="text-xs text-red-600">{cancelError}</p>
                    )}
                  </div>
                )}

                {hasPaidSub && subStatus === SUBSCRIPTION_STATUS.PAST_DUE && (
                  <div className="mt-3 space-y-2">
                    <button
                      onClick={handleCancelSubscription}
                      disabled={cancelling}
                      className="text-sm text-red-600 hover:text-red-700 font-medium flex items-center gap-1.5 transition-colors"
                    >
                      {cancelling ? <EcointelLottieMark size={14} /> : <XCircle size={14} />}
                      {cancelling ? 'Cancelling...' : 'Cancel Subscription'}
                    </button>
                    <p className="text-xs text-slate-500">
                      Cancel your subscription before deleting your account.
                    </p>
                    {cancelError && (
                      <p className="text-xs text-red-600">{cancelError}</p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Consents & permissions (handoff 11) */}
          <ConsentsCard profile={profile} />

          {/* Account deletion */}
          <div className="border-t border-slate-100 pt-6">
            <h3 className="font-bold text-slate-900 mb-2">Delete account</h3>
            {isAdminUser ? (
              <p className="text-sm text-slate-600">
                Admin accounts cannot be self-deleted from this page. Ask another administrator or contact{' '}
                <a href="mailto:accounts@ecointel.io" className="text-[#3d9dad] hover:underline">accounts@ecointel.io</a>.
              </p>
            ) : (
              <>
                {mustCancelBeforeDelete ? (
                  <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-4 space-y-3">
                    <div className="flex items-start gap-2 text-sm text-amber-900">
                      <AlertTriangle size={16} className="shrink-0 mt-0.5 text-amber-600" />
                      <div>
                        <p className="font-semibold">Cancel your subscription first</p>
                        <p className="mt-1 text-amber-800">
                          To avoid ongoing billing, use <strong>Cancel Subscription</strong> above before deleting your account.
                          That schedules billing to stop at the end of your current period. Once your status shows <strong>Cancelling</strong>, you can return here to delete your account.
                        </p>
                      </div>
                    </div>
                    {hasPaidSub && (subStatus === SUBSCRIPTION_STATUS.ACTIVE || subStatus === SUBSCRIPTION_STATUS.PAST_DUE) && (
                      <button
                        type="button"
                        onClick={handleCancelSubscription}
                        disabled={cancelling}
                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-amber-900 border border-amber-300 bg-white rounded-lg hover:bg-amber-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {cancelling ? <EcointelLottieMark size={14} /> : <XCircle size={14} />}
                        {cancelling ? 'Scheduling cancellation…' : 'Cancel subscription'}
                      </button>
                    )}
                  </div>
                ) : isCancelling ? (
                  <p className="text-sm text-slate-600 mb-4">
                    Your subscription is set to end
                    {cancellingEndDateLabel ? <> on <strong>{cancellingEndDateLabel}</strong></> : ' at the end of your current billing period'}
                    {' '}and will not renew. You can delete your account now.
                  </p>
                ) : (
                  <p className="text-sm text-slate-600 mb-4">
                    Permanently remove your profile and sign-in access. This does not delete property data held in the app.
                  </p>
                )}

                <button
                  type="button"
                  onClick={handleDeleteAccount}
                  disabled={deletingAccount || !canDeleteAccount}
                  title={mustCancelBeforeDelete ? 'Cancel your subscription first' : undefined}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-red-700 border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {deletingAccount ? <EcointelLottieMark size={14} /> : <Trash2 size={14} />}
                  {deletingAccount ? 'Deleting account…' : 'Delete account'}
                </button>
                {deleteAccountError ? (
                  <p className="text-xs text-red-600 mt-2">{deleteAccountError}</p>
                ) : null}
              </>
            )}
          </div>

          {/* Developer Controls — admin only, non-production only */}
          {isAdminUser && process.env.NODE_ENV !== 'production' && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <div className="flex items-center gap-2 text-amber-800 font-bold mb-4">
                <AlertTriangle size={20} />
                <h3>Developer Controls (Local Only)</h3>
              </div>
              <p className="text-sm text-amber-700 mb-6">
                These controls are hidden in production builds.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={async () => {
                    await updateDoc(doc(db, "users", auth.currentUser.uid), { subscriptionStatus: 'active' });
                    window.location.reload();
                  }}
                  className="px-4 py-2 bg-white border border-amber-200 text-amber-900 rounded-lg hover:bg-amber-100 font-medium text-sm"
                >
                  Force Active
                </button>
                <button
                  onClick={async () => {
                    await updateDoc(doc(db, "users", auth.currentUser.uid), { subscriptionStatus: 'none', stripeSubscriptionId: '', stripeCustomerId: '' });
                    window.location.reload();
                  }}
                  className="px-4 py-2 bg-white border border-amber-200 text-amber-900 rounded-lg hover:bg-amber-100 font-medium text-sm"
                >
                  Force No Subscription
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
